import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useOmnichannelConversations,
  useConversationMessages,
  useOmnichannelStatus,
  useOmnichannelAccounts,
  markOmnichannelConversationRead,
} from '@/api';
import { OMNICHANNEL_CONVERSATIONS_QUERY_KEY } from '@/api/queries/omnichannel/key';
import { OMNICHANNEL_POLL_INTERVAL_MS } from '@/lib/omnichannel/polling';
import { refreshLatestOmnichannelMessagesCache } from '@/lib/omnichannel/refresh-latest-messages-cache';
import {
  getInitials,
  transformConversation,
  transformMessages,
} from '../utils/transformers';
import type {
  ChannelType,
  OmnichannelAccount,
  OmnichannelConversation,
  OmnichannelConversationLegacy,
} from '@/types';

type ChannelStatus = boolean | { pageId?: string };

const getChannelStatus = (
  status: ChannelStatus | undefined
): { connected: boolean; pageId?: string } => {
  if (!status) return { connected: false };
  if (typeof status === 'boolean') return { connected: status };
  return { connected: !!status.pageId, pageId: status.pageId };
};

const getPageIdFromAccounts = (
  accounts: OmnichannelAccount[] | undefined,
  channel: ChannelType,
  fallbackAccounts?: OmnichannelAccount[]
): string | undefined => {
  if (!accounts || accounts.length === 0) {
    if (channel === 'instagram' && fallbackAccounts) {
      const accountWithInstagram = fallbackAccounts.find(
        (acc) => acc.instagram_business_account
      );
      return accountWithInstagram?.instagram_business_account?.id;
    }
    return undefined;
  }

  if (channel === 'instagram') {
    const accountWithInstagram = accounts.find(
      (acc) => acc.instagram_business_account
    );
    return (
      accountWithInstagram?.instagram_business_account?.id ||
      accounts[0]?.pageId
    );
  }

  return accounts[0]?.pageId;
};

const getBusinessId = (
  conversations: OmnichannelConversation[] | undefined
): string | undefined => {
  if (
    !conversations ||
    !Array.isArray(conversations) ||
    conversations.length === 0
  ) {
    return undefined;
  }

  const validConversations = conversations.filter(
    (conv): conv is OmnichannelConversationLegacy =>
      conv != null && 'senders' in conv && !!conv.senders?.data
  );
  if (validConversations.length === 0) {
    return undefined;
  }

  const firstConversation = validConversations[0];
  if (!firstConversation.senders?.data) {
    return undefined;
  }

  const potentialBusinessIds = firstConversation.senders.data.map((s) => s.id);

  const businessId = potentialBusinessIds.find((id: string) =>
    validConversations.every((conv) =>
      conv.senders?.data?.some((sender) => sender.id === id)
    )
  );

  return businessId || firstConversation.senders.data[1]?.id;
};

/** Sum of unread counts from transformed conversations (for tab badges) */
const getUnreadSum = (
  conversationList: Array<{ unread?: number }> | undefined
): number => {
  if (!conversationList || !Array.isArray(conversationList)) return 0;
  return conversationList.reduce((sum, c) => sum + (c.unread ?? 0), 0);
};

export const useConversationData = (
  currentChannel: ChannelType,
  selectedConversation: string,
  activeTab: ChannelType | 'all',
  selectedPageId?: string
) => {
  const { data: statusData, isLoading: isLoadingStatus } =
    useOmnichannelStatus();

  const status = statusData as
    | {
        facebook?: ChannelStatus;
        instagram?: ChannelStatus;
        whatsapp?: ChannelStatus;
      }
    | undefined;

  const channelStatuses = useMemo(() => {
    if (!status) {
      return {
        facebook: { connected: false },
        instagram: { connected: false },
        whatsapp: { connected: false },
      };
    }
    return {
      facebook: getChannelStatus(status.facebook),
      instagram: getChannelStatus(status.instagram),
      whatsapp: getChannelStatus(status.whatsapp),
    };
  }, [status]);

  const isCurrentChannelConnected = useMemo(() => {
    const channelMap = {
      facebook: channelStatuses.facebook,
      instagram: channelStatuses.instagram,
      whatsapp: channelStatuses.whatsapp,
    };
    return channelMap[currentChannel]?.connected || false;
  }, [currentChannel, channelStatuses]);

  const { data: accounts, isLoading: isLoadingAccounts } =
    useOmnichannelAccounts({
      channel: currentChannel,
      enabled: isCurrentChannelConnected && activeTab !== 'all',
    });

  const { data: facebookAccounts } = useOmnichannelAccounts({
    channel: 'facebook',
    enabled: channelStatuses.facebook.connected,
  });
  const { data: instagramAccounts } = useOmnichannelAccounts({
    channel: 'instagram',
    enabled: channelStatuses.instagram.connected,
  });
  const { data: whatsappAccounts } = useOmnichannelAccounts({
    channel: 'whatsapp',
    enabled: channelStatuses.whatsapp.connected,
  });

  const facebookPageId = useMemo(
    () => getPageIdFromAccounts(facebookAccounts, 'facebook'),
    [facebookAccounts]
  );

  const instagramPageId = useMemo(
    () =>
      getPageIdFromAccounts(instagramAccounts, 'instagram', facebookAccounts),
    [instagramAccounts, facebookAccounts]
  );

  const whatsappPageId = useMemo(
    () => getPageIdFromAccounts(whatsappAccounts, 'whatsapp'),
    [whatsappAccounts]
  );

  const {
    data: facebookConversationsData,
    isLoading: isLoadingFacebookConversations,
  } = useOmnichannelConversations({
    channel: 'facebook',
    pageId: facebookPageId,
    enabled: channelStatuses.facebook.connected && !!facebookPageId,
  });

  const {
    data: instagramConversationsData,
    isLoading: isLoadingInstagramConversations,
  } = useOmnichannelConversations({
    channel: 'instagram',
    pageId: instagramPageId,
    enabled: channelStatuses.instagram.connected && !!instagramPageId,
  });

  const {
    data: whatsappConversationsData,
    isLoading: isLoadingWhatsappConversations,
  } = useOmnichannelConversations({
    channel: 'whatsapp',
    pageId: whatsappPageId,
    enabled: channelStatuses.whatsapp.connected && !!whatsappPageId,
  });

  const pageIdByChannel = useMemo(
    () => ({
      facebook: facebookPageId,
      instagram: instagramPageId,
      whatsapp: whatsappPageId,
    }),
    [facebookPageId, instagramPageId, whatsappPageId]
  );

  const effectiveChannelForMessages = useMemo<ChannelType>(() => {
    if (activeTab !== 'all') return currentChannel;
    const id = selectedConversation;
    if (!id) return currentChannel;

    const inFacebook = facebookConversationsData?.data?.some(
      (c) => c?.id === id
    );
    if (inFacebook) return 'facebook';

    const inInstagram = instagramConversationsData?.data?.some(
      (c) => c?.id === id
    );
    if (inInstagram) return 'instagram';

    const inWhatsapp = whatsappConversationsData?.data?.some(
      (c) => c?.id === id
    );
    if (inWhatsapp) return 'whatsapp';

    return currentChannel;
  }, [
    activeTab,
    currentChannel,
    selectedConversation,
    facebookConversationsData,
    instagramConversationsData,
    whatsappConversationsData,
  ]);

  const pageId = useMemo(() => {
    if (selectedPageId) return selectedPageId;
    return getPageIdFromAccounts(accounts, currentChannel);
  }, [accounts, currentChannel, selectedPageId]);

  const resolvedPageIdForMessages = useMemo(() => {
    if (selectedPageId) return selectedPageId;
    if (activeTab !== 'all') return pageId;
    return pageIdByChannel[effectiveChannelForMessages];
  }, [
    selectedPageId,
    activeTab,
    pageId,
    pageIdByChannel,
    effectiveChannelForMessages,
  ]);

  const effectiveChannelConnected = useMemo(() => {
    const map = {
      facebook: channelStatuses.facebook.connected,
      instagram: channelStatuses.instagram.connected,
      whatsapp: channelStatuses.whatsapp.connected,
    };
    return map[effectiveChannelForMessages] ?? false;
  }, [channelStatuses, effectiveChannelForMessages]);

  const businessId = useMemo(() => {
    const selected =
      effectiveChannelForMessages === 'facebook'
        ? facebookConversationsData?.data
        : effectiveChannelForMessages === 'instagram'
          ? instagramConversationsData?.data
          : whatsappConversationsData?.data;
    return getBusinessId(selected) ?? resolvedPageIdForMessages;
  }, [
    effectiveChannelForMessages,
    facebookConversationsData,
    instagramConversationsData,
    whatsappConversationsData,
    resolvedPageIdForMessages,
  ]);

  const messagesQuery = useConversationMessages({
    conversationId: selectedConversation,
    channel: effectiveChannelForMessages,
    pageId: resolvedPageIdForMessages,
    enabled:
      !!selectedConversation &&
      effectiveChannelConnected &&
      !!resolvedPageIdForMessages,
  });
  const messagesData = messagesQuery.data;
  const queryClient = useQueryClient();
  const [isRefreshingLatestMessages, setIsRefreshingLatestMessages] =
    useState(false);

  const refreshLatestMessages = useCallback(async () => {
    if (
      !selectedConversation ||
      !effectiveChannelConnected ||
      !resolvedPageIdForMessages
    ) {
      return;
    }

    setIsRefreshingLatestMessages(true);
    try {
      await refreshLatestOmnichannelMessagesCache(queryClient, {
        conversationId: selectedConversation,
        channel: effectiveChannelForMessages,
        pageId: resolvedPageIdForMessages,
      });
    } finally {
      setIsRefreshingLatestMessages(false);
    }
  }, [
    selectedConversation,
    effectiveChannelConnected,
    resolvedPageIdForMessages,
    effectiveChannelForMessages,
    queryClient,
  ]);

  /** Poll only the newest page; older pages are loaded once and left unchanged. */
  useEffect(() => {
    if (
      !selectedConversation ||
      !effectiveChannelConnected ||
      !resolvedPageIdForMessages
    ) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refreshLatestMessages();
    }, OMNICHANNEL_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [
    selectedConversation,
    effectiveChannelConnected,
    resolvedPageIdForMessages,
    refreshLatestMessages,
  ]);

  /** Load the full thread automatically (no manual "load older" in the UI). */
  const {
    fetchPreviousPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    isLoading: isLoadingMessages,
  } = messagesQuery;

  useEffect(() => {
    if (!selectedConversation) return;
    if (isLoadingMessages) return;
    if (!hasPreviousPage) return;
    if (isFetchingPreviousPage) return;

    void fetchPreviousPage();
  }, [
    selectedConversation,
    isLoadingMessages,
    hasPreviousPage,
    isFetchingPreviousPage,
    messagesData?.pages.length,
    fetchPreviousPage,
  ]);

  const businessIdsByChannel = useMemo(() => {
    return {
      facebook:
        getBusinessId(facebookConversationsData?.data) ?? facebookPageId,
      instagram:
        getBusinessId(instagramConversationsData?.data) ?? instagramPageId,
      whatsapp:
        getBusinessId(whatsappConversationsData?.data) ?? whatsappPageId,
    } as const;
  }, [
    facebookConversationsData,
    instagramConversationsData,
    whatsappConversationsData,
    facebookPageId,
    instagramPageId,
    whatsappPageId,
  ]);

  const transformChannelConversations = useMemo(() => {
    const toList = (
      arr: OmnichannelConversation[] | undefined,
      channel: ChannelType
    ) =>
      (arr ?? [])
        .filter((conv) => conv != null && typeof conv === 'object')
        .map((conv) =>
          transformConversation(
            conv,
            channel,
            businessIdsByChannel[channel] ?? pageIdByChannel[channel]
          )
        );

    return {
      facebook: toList(facebookConversationsData?.data, 'facebook'),
      instagram: toList(instagramConversationsData?.data, 'instagram'),
      whatsapp: toList(whatsappConversationsData?.data, 'whatsapp'),
    };
  }, [
    facebookConversationsData,
    instagramConversationsData,
    whatsappConversationsData,
    businessIdsByChannel,
    pageIdByChannel,
  ]);

  const baseConversations = useMemo(() => {
    const { facebook, instagram, whatsapp } = transformChannelConversations;
    const recency = (lastMessageAt?: string) => {
      const time = lastMessageAt ? new Date(lastMessageAt).getTime() : 0;
      return Number.isFinite(time) ? time : 0;
    };
    const byRecency = <T extends { lastMessageAt?: string }>(items: T[]) =>
      [...items].sort(
        (a, b) => recency(b.lastMessageAt) - recency(a.lastMessageAt)
      );

    if (activeTab === 'all') {
      return byRecency([...facebook, ...instagram, ...whatsapp]);
    }

    if (activeTab === 'facebook') return byRecency(facebook);
    if (activeTab === 'instagram') return byRecency(instagram);
    return byRecency(whatsapp);
  }, [activeTab, transformChannelConversations]);

  const selectedConvFromList = useMemo(
    () => baseConversations.find((c) => c.id === selectedConversation) ?? null,
    [baseConversations, selectedConversation]
  );

  /** Mark conversation read when opened (clears unread badge). */
  const markedReadConversationIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!selectedConversation) return;
    const unread = selectedConvFromList?.unread ?? 0;
    if (unread <= 0) return;
    if (markedReadConversationIdsRef.current.has(selectedConversation)) return;

    const conversationId = selectedConversation;
    markedReadConversationIdsRef.current.add(conversationId);
    let cancelled = false;

    void markOmnichannelConversationRead(conversationId)
      .then(() => {
        if (cancelled) return;
        void queryClient.invalidateQueries({
          queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
        });
      })
      .catch(() => {
        markedReadConversationIdsRef.current.delete(conversationId);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedConversation, selectedConvFromList?.unread, queryClient]);

  /** When the conversations list shows a newer preview than loaded thread messages, refetch once per preview revision. */
  const threadSyncRefetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedConversation || !selectedConvFromList?.lastMessageAt) return;

    const previewMs = new Date(selectedConvFromList.lastMessageAt).getTime();
    if (Number.isNaN(previewMs)) return;

    const pages = messagesData?.pages ?? [];
    const flattened = pages.flatMap((p) => p?.data ?? []);
    let latestInThreadMs = 0;
    for (const m of flattened) {
      const t = m.created_time ? new Date(m.created_time).getTime() : 0;
      if (t > latestInThreadMs) latestInThreadMs = t;
    }

    const STALE_MS = 2000;
    const key = `${selectedConversation}:${selectedConvFromList.lastMessageAt}`;

    if (previewMs <= latestInThreadMs + STALE_MS) {
      if (threadSyncRefetchKeyRef.current === key) {
        threadSyncRefetchKeyRef.current = null;
      }
      return;
    }

    if (threadSyncRefetchKeyRef.current === key) return;
    threadSyncRefetchKeyRef.current = key;
    void refreshLatestMessages();
  }, [
    selectedConversation,
    selectedConvFromList?.lastMessageAt,
    messagesData?.pages,
    refreshLatestMessages,
  ]);

  const businessInitials = useMemo(() => {
    if (activeTab !== 'all') {
      if (!pageId && !accounts?.length) return undefined;
      const account =
        accounts?.find((acc) => acc.pageId === pageId) ?? accounts?.[0];
      return account ? getInitials(account.name ?? 'You') : undefined;
    }

    const channel = effectiveChannelForMessages;
    const candidateAccounts =
      channel === 'facebook'
        ? facebookAccounts
        : channel === 'instagram'
          ? instagramAccounts
          : whatsappAccounts;

    const resolved = resolvedPageIdForMessages;
    const account =
      candidateAccounts?.find((acc) => acc.pageId === resolved) ??
      candidateAccounts?.[0];
    return account ? getInitials(account.name ?? 'You') : undefined;
  }, [
    activeTab,
    pageId,
    accounts,
    effectiveChannelForMessages,
    facebookAccounts,
    instagramAccounts,
    whatsappAccounts,
    resolvedPageIdForMessages,
  ]);

  const messages = useMemo(() => {
    const pages = messagesData?.pages ?? [];
    const flattened = pages.flatMap((p) => p?.data ?? []);
    const byId = new Map<string, (typeof flattened)[number]>();
    for (const message of flattened) {
      const id = message?.id;
      if (!id) continue;
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, message);
        continue;
      }
      const existingTime = existing.created_time
        ? new Date(existing.created_time).getTime()
        : 0;
      const nextTime = message.created_time
        ? new Date(message.created_time).getTime()
        : 0;
      if (nextTime >= existingTime) byId.set(id, message);
    }
    const unique = Array.from(byId.values());

    return unique.length && selectedConversation
      ? transformMessages(
          unique,
          effectiveChannelForMessages,
          selectedConversation,
          businessId,
          {
            customerInitials: selectedConvFromList?.initials,
            businessInitials,
          }
        )
      : [];
  }, [
    messagesData?.pages,
    effectiveChannelForMessages,
    selectedConversation,
    businessId,
    selectedConvFromList?.initials,
    businessInitials,
  ]);

  const conversations = baseConversations;

  // Tab badges show unread counts per channel regardless of active tab
  const conversationCounts = useMemo(() => {
    const { facebook, instagram, whatsapp } = transformChannelConversations;

    return {
      all: getUnreadSum([...facebook, ...instagram, ...whatsapp]),
      facebook: getUnreadSum(facebook),
      instagram: getUnreadSum(instagram),
      whatsapp: getUnreadSum(whatsapp),
    };
  }, [transformChannelConversations]);

  return {
    conversations,
    messages,
    messagesQuery,
    refreshLatestMessages,
    isRefreshingLatestMessages,
    businessId,
    conversationsData:
      activeTab === 'all'
        ? []
        : currentChannel === 'facebook'
          ? facebookConversationsData
          : currentChannel === 'instagram'
            ? instagramConversationsData
            : whatsappConversationsData,
    conversationsRawData:
      activeTab === 'all'
        ? [
            ...(facebookConversationsData?.data ?? []),
            ...(instagramConversationsData?.data ?? []),
            ...(whatsappConversationsData?.data ?? []),
          ]
        : ((currentChannel === 'facebook'
            ? facebookConversationsData?.data
            : currentChannel === 'instagram'
              ? instagramConversationsData?.data
              : whatsappConversationsData?.data) ?? []),
    isLoadingConversations:
      activeTab === 'all'
        ? isLoadingFacebookConversations ||
          isLoadingInstagramConversations ||
          isLoadingWhatsappConversations
        : currentChannel === 'facebook'
          ? isLoadingFacebookConversations
          : currentChannel === 'instagram'
            ? isLoadingInstagramConversations
            : isLoadingWhatsappConversations,
    isLoadingAccounts: isLoadingStatus || isLoadingAccounts,
    hasFacebookAccount: channelStatuses.facebook.connected,
    hasInstagramAccount: channelStatuses.instagram.connected,
    hasWhatsappAccount: channelStatuses.whatsapp.connected,
    isCurrentChannelConnected: effectiveChannelConnected,
    accounts: activeTab === 'all' ? [] : accounts,
    conversationCounts,
    resolvedPageIdForMessages,
  };
};
